import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { Schedule } from '@data/fms/schedule/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 休止（メンテナンス）スケジュール登録API
 */
export const useMaintenanceSchedule = (): {
  state: APIState;
  postMaintenanceSchedule: (
    vehicleId: string,
    startTime: string,
    endTime: string,
  ) => Promise<Schedule | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postMaintenanceScheduleAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      startTime: string,
      endTime: string,
    ): Promise<Result<Schedule, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/maintenance`,
          {
            start_time: startTime,
            end_time: endTime,
            tags: [{ key: 'app_name', value: 'FMS Console' }],
          },
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postMaintenanceSchedule = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, startTime: string, endTime: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_maintenance_schedule', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await postMaintenanceScheduleAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          startTime,
          endTime,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_maintenance_schedule', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.post_maintenance_schedule', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        notifySuccess,
        postMaintenanceScheduleAPI,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    postMaintenanceSchedule,
  };
};
